import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authAPI } from '../api/services';
import { AuthState, LoginCredentials, ChangePasswordData } from '../types';

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  loading: false,
  error: null,
  requiresOrgSelection: false,
  orgOptions: [],
};

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(credentials);
      // Always store token — for requiresOrgSelection it's a temp token that gets
      // replaced by the org-scoped JWT after /select-org completes
      localStorage.setItem('token', response.data.token);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

export const selectOrg = createAsyncThunk(
  'auth/selectOrg',
  async (orgId: string, { rejectWithValue }) => {
    try {
      const response = await authAPI.selectOrg(orgId);
      localStorage.setItem('token', response.data.token);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Organisation selection failed');
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (data: ChangePasswordData, { rejectWithValue }) => {
    try {
      const response = await authAPI.changePassword(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Password change failed');
    }
  }
);

export const getProfile = createAsyncThunk(
  'auth/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.getProfile();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get profile');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.requiresOrgSelection = false;
      state.orgOptions = [];
      localStorage.removeItem('token');
    },
    clearError: (state) => {
      state.error = null;
    },
    resetOrgSelection: (state) => {
      state.requiresOrgSelection = false;
      state.orgOptions = [];
      state.user = null;
      state.token = null;
      localStorage.removeItem('token');
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.requiresOrgSelection) {
          state.requiresOrgSelection = true;
          state.orgOptions = action.payload.orgOptions;
          state.token = action.payload.token;
          state.user = action.payload.user;
          // isAuthenticated stays false until org is selected
        } else {
          state.isAuthenticated = true;
          state.requiresOrgSelection = false;
          state.orgOptions = [];
          state.user = action.payload.user;
          state.token = action.payload.token;
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Get Profile
      .addCase(getProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Change Password
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
        if (state.user) {
          state.user.isFirstLogin = false;
        }
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Select Org
      .addCase(selectOrg.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(selectOrg.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.requiresOrgSelection = false;
        state.orgOptions = [];
        state.token = action.payload.token;
        state.user = action.payload.user;
      })
      .addCase(selectOrg.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout, clearError, resetOrgSelection } = authSlice.actions;
export default authSlice.reducer;
